// Universal renderer wrapper — draws a title + quip (both optional) and
// an optional collapse toggle. Individual blocks focus purely on their body.

import { hoardify } from "../Hoard";
import { useState } from "react";
import { ChevronDown } from "lucide-react";

export const BlockShell = ({ block, children, accent = "#2E5C55" }) => {
  const locked = Boolean(block.config?.locked_closed);
  const [open, setOpen] = useState(!block.collapsed_by_default && !locked);
  const hasHeader = block.title || block.quip;
  const onDark = String(accent).toLowerCase() === "#ffffff";
  const quipCls = onDark ? "text-white/80" : "text-[#5C5C5C]";
  const collapsible = !locked && (block.collapsed_by_default || block.quip);
  return (
    <div id={block.config?.anchor || undefined} className="w-full scroll-mt-24" data-testid={`block-${block.type}-${block.block_id}`}>
      {hasHeader && (
        <div className="mb-4">
          {block.title && (
            <button
              type="button"
              onClick={() => collapsible && setOpen(!open)}
              className={`flex items-start gap-3 text-left w-full ${collapsible ? "cursor-pointer" : "cursor-default"}`}
            >
              {block.config?.title_image && <img src={block.config.title_image} alt="" className="h-14 w-14 md:h-20 md:w-20 rounded-2xl object-cover shrink-0 shadow-md" data-testid="block-title-image" />}
              <div className="flex-1">
                <h2
                  className="font-['Playfair_Display'] text-2xl md:text-3xl font-semibold whitespace-pre-line"
                  style={{ color: block.config?.title_color || accent }}
                >
                  {hoardify(block.title)}
                </h2>
                {block.quip && (
                  <p className={block.config?.big_quip ? `text-xl md:text-2xl font-bold ${onDark ? "text-white" : "text-[#1A1A1A]"} mt-2 leading-snug` : `text-base md:text-lg ${quipCls} italic mt-1 leading-snug`} data-testid="block-quip">
                    {hoardify(block.quip)}
                  </p>
                )}
              </div>
              {collapsible && (
                <ChevronDown
                  className={`h-5 w-5 mt-2 ${quipCls} transition-transform ${open ? "rotate-180" : ""}`}
                />
              )}
            </button>
          )}
          {!block.title && block.quip && (
            <p className={`text-base md:text-lg ${quipCls} italic leading-snug`}>
              {hoardify(block.quip)}
            </p>
          )}
        </div>
      )}
      {collapsible && block.title && block.collapsed_by_default && (
        <button type="button" onClick={() => setOpen(!open)} className={`-mt-2 mb-4 inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${onDark ? "border-white/40 text-white hover:bg-white/10" : "hover:text-white"}`} style={onDark ? undefined : { borderColor: accent, color: accent, "--acc": accent }} onMouseEnter={(e) => { if (!onDark) e.currentTarget.style.backgroundColor = accent; }} onMouseLeave={(e) => { if (!onDark) e.currentTarget.style.backgroundColor = ""; }} data-testid={`block-readmore-${block.block_id}`}>
          {open ? (block.config?.read_less_label || "Show less") : (block.config?.read_more_label || "Read more")}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}
      {open && !locked && children}
    </div>
  );
};

export default BlockShell;
